import { Grid, Typography } from "@mui/material";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import DialogContentWrapper from "../../../../../../common/components/functional/dialogs/DialogContentWrapper";
import { POSITIVE_INTEGER_REGEX } from "../../../../../../common/helpers/regexes";
import {
  PRICING_SPECIAL_PATH_START,
  PricingCategory,
  PricingSpecialSetting,
  PricingSpecialsPricingRuleType,
} from "../../../constants";
import LabelInputRow from "../../../form/LabelInputRow";
import LabelSelectRow from "../../../form/LabelSelectRow";
import { getPricingObjectForSpecialRulesAndPricing } from "../../../helpers/pricingUtils";
import { UnsavedChangesProvider } from "../../../../../../contexts/UnsavedChangesContext";
import { PricingGuideText } from "../../../styles";
import { useState } from "react";
import { ModalState } from "../../../../types/types";
import DialogComponent from "../../../../../../common/components/functional/dialogs/DialogComponent";
import { DEFAULT_DRAG_HANDLE_SELECTOR } from "../../../../../../app/constants";
import PricingsByExpenseTypeForm from "../workAndAbscences/PricingsByExpenseTypeForm";
import isEmpty from "../../../../../../common/helpers/isEmpty";

type Props = {
  tesId?: string | null;
  disabled?: boolean;
  canEdit: boolean;
  onClose: () => void;
};

const MAIN_FORM_PROPERTY_KEY = `${PRICING_SPECIAL_PATH_START}.${PricingSpecialSetting.ExpressOrder}`;
const { BY_SHIFT, BY_CATEGORY } = PricingSpecialsPricingRuleType;
const PRICING_OPTIONS = [BY_SHIFT, BY_CATEGORY];
const DEFAULT_SELECTION = BY_SHIFT;

const DRAG_HANDLE_SELECTOR = "drag-handle-express-order-rules-and-pricing";

const ExpressOrderRulesAndPricingForm = ({
  tesId,
  disabled,
  canEdit,
  onClose,
}: Props) => {
  const { t } = useTranslation("customers");
  const { getValues: getMainFormValues, setValue: setMainFormValue } =
    useFormContext();

  const mainFormValues = getMainFormValues(MAIN_FORM_PROPERTY_KEY);
  const hours = mainFormValues?.settings?.hours;
  const pricing = mainFormValues?.settings?.pricing;

  const [modalState, setModalState] = useState<ModalState>({
    show: false,
    id: "",
  });

  const onOpenModal = (id?: string) => setModalState({ show: true, id });
  const onCloseModal = () =>
    setModalState((prevState) => ({ ...prevState, show: false }));
  const onModalExited = () => {
    setModalState({ show: false, id: "" });
  };

  const getPricingBySalaryTypeModalDefaultProps = (
    categoryKey: PricingCategory,
  ) => {
    return {
      tesId: tesId!, // cannot be null here
      mainFormPropertyKey: categoryKey,
      expenseTypeCategoryFilter: categoryKey,
      modalTitle: t("contract.pricingBySalaryType"),
      rowLabel: t("contract.salaryType"),
      inputLabel: t("contract.searchSalaryTypes"),
      addButtonLabel: t("contract.pricingBySalaryType"),
      confirmDialogTitle: t("contract.pricingBySalaryTypeDeleteConfirmTitle"),
      confirmDialogContentText: t(
        "contract.pricingBySalaryTypeDeleteConfirmText",
      ),
      onClose: onCloseModal,
      disabled,
      canEdit,
      dragHandleSelector: DRAG_HANDLE_SELECTOR,
      hideOverrideOption: true,
    };
  };

  const getModalContent = () => {
    switch (modalState.id) {
      case PricingCategory.BaseHours:
        return (
          <PricingsByExpenseTypeForm
            {...getPricingBySalaryTypeModalDefaultProps(
              PricingCategory.BaseHours,
            )}
          />
        );
      case PricingCategory.Supplements:
        return (
          <PricingsByExpenseTypeForm
            {...getPricingBySalaryTypeModalDefaultProps(
              PricingCategory.Supplements,
            )}
          />
        );
      default:
        return <></>;
    }
  };

  const defaultValues = {
    hours: hours || "",
    pricingSelection: pricing?.type || DEFAULT_SELECTION,
    costPerShift: pricing?.shift || "",
    baseHours: {
      multiplier: pricing?.baseHours?.multiplier?.value || "",
      expenses: pricing?.baseHours?.expenses,
    },
    supplements: {
      multiplier: pricing?.supplements?.multiplier?.value || "",
      expenses: pricing?.supplements?.expenses,
    },
  };

  const editButtonText = t("contract.pricingBySalaryType");
  const viewButtonText = t("contract.showPricingBySalaryTypeInformation");

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const {
    watch,
    handleSubmit,
    formState: { isDirty },
  } = methods;

  const selectedOption = watch("pricingSelection");
  const byShiftSelected = selectedOption === BY_SHIFT;

  const onSubmit = (formData: any) => {
    const updatedPricingObject = {
      enabled: mainFormValues.enabled,
      settings: {
        hours: formData.hours,
        pricing: getPricingObjectForSpecialRulesAndPricing({
          byShiftSelected,
          formData,
        }),
      },
    };

    setMainFormValue(MAIN_FORM_PROPERTY_KEY, updatedPricingObject, {
      shouldDirty: true,
    });

    onClose();
  };

  const onSave = () => {
    handleSubmit(onSubmit)();
  };

  const onCancel = () => onClose();

  const watchedHours = watch("hours");
  const watchedCostPerShift = watch("costPerShift");
  const watchedBaseHours = watch("baseHours");
  const watchedSupplements = watch("supplements");

  const MAX_HOURS = 10000;
  const isHoursInputValid =
    POSITIVE_INTEGER_REGEX.test(watchedHours) && watchedHours <= MAX_HOURS;

  const byShiftIsValid = isHoursInputValid && !!watchedCostPerShift;
  const byMultiplierIsValid =
    isHoursInputValid &&
    !!watchedBaseHours.multiplier &&
    !!watchedSupplements.multiplier;

  const saveDisabled =
    !isDirty || byShiftSelected ? !byShiftIsValid : !byMultiplierIsValid;

  const showBaseHoursViewButton = !isEmpty(watchedBaseHours.expenses);
  const showSupplementsViewButton = !isEmpty(watchedSupplements.expenses);

  return (
    <DialogContentWrapper
      title={t("contract.expressOrderRulesAndPricing")}
      margin
      onCancel={onCancel}
      onSave={onSave}
      saveDisabled={saveDisabled}
    >
      <FormProvider {...methods}>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <PricingGuideText paddingTop="0">
            {t("contract.expressOrderGuideText")}
          </PricingGuideText>
          <LabelInputRow
            label={t("contract.amountOfHours")}
            labelWidth={3}
            fieldName="hours"
            inputType="number"
            inputWidth={3}
            inputSpec={t("contract.hours")}
            inputError={!isHoursInputValid}
            disabled={disabled}
            decimalScale={3}
          />
          <Grid container sx={{ margin: "-0.5rem 0 1rem 1rem" }}>
            <Grid item xs={3} />
            <Grid item xs={7}>
              <Typography variant="secondary" fontSize="12px">
                {t("contract.hourRuleDescription")}
              </Typography>
            </Grid>
          </Grid>
          <LabelSelectRow
            label={t("contract.pricingAlt")}
            labelWidth={3}
            fieldName="pricingSelection"
            options={PRICING_OPTIONS}
            getDisplayValue={(val) => t(`contract.${val}`)}
            selectWidth={6}
            disabled={disabled}
          />
          {byShiftSelected ? (
            <LabelInputRow
              label=""
              labelWidth={3}
              fieldName="costPerShift"
              fieldType="number"
              inputWidth={3}
              inputSuffix="€"
              inputSpec={t("contract.perShift")}
              inputError={!watchedCostPerShift}
              disabled={disabled}
              decimalScale={3}
            />
          ) : (
            <>
              <LabelInputRow
                label={t("settings:BaseHours")}
                labelWidth={3}
                fieldName="baseHours.multiplier"
                fieldType="number"
                inputWidth={3}
                inputSpec={t("contract.multiplier").toLowerCase()}
                inputError={!watchedBaseHours.multiplier}
                disabled={disabled}
                decimalScale={3}
                viewButtonText={viewButtonText}
                editButtonText={editButtonText}
                onOpenModal={() => onOpenModal(PricingCategory.BaseHours)}
                showButton
                showViewButton={showBaseHoursViewButton}
              />

              <LabelInputRow
                label={t("settings:Supplements")}
                labelWidth={3}
                fieldName="supplements.multiplier"
                fieldType="number"
                inputWidth={3}
                inputSpec={t("contract.multiplier").toLowerCase()}
                inputError={!watchedSupplements.multiplier}
                disabled={disabled}
                decimalScale={3}
                viewButtonText={viewButtonText}
                editButtonText={editButtonText}
                onOpenModal={() => onOpenModal(PricingCategory.Supplements)}
                showButton
                showViewButton={showSupplementsViewButton}
              />
            </>
          )}
        </UnsavedChangesProvider>
        <DialogComponent
          open={modalState.show}
          onClose={onCloseModal}
          sx={{ display: "flex", maxHeight: "80%" }}
          dragHandleSelector={DEFAULT_DRAG_HANDLE_SELECTOR}
          TransitionProps={{ onExited: onModalExited }}
        >
          {getModalContent()}
        </DialogComponent>
      </FormProvider>
    </DialogContentWrapper>
  );
};

export default ExpressOrderRulesAndPricingForm;
